import { useCallback, useEffect, useRef, useState } from 'react';
import { useAutoRefresh } from './useAutoRefresh';
import { api } from '@/api/client';
import type { Job, JobListParams, JobSummary } from '@/api/types';
import { mergeJobRows, shouldPollJobsFast, summaryTotalJobs } from '@/utils/jobStatus';
import {
  JOBS_POLL_BURST_WINDOW_MS,
  pollIntervalMs,
} from './useJobsListPolling.utils';

export {
  JOBS_POLL_ACTIVE_MS,
  JOBS_POLL_BURST_WINDOW_MS,
  JOBS_POLL_HIDDEN_MS,
  JOBS_POLL_IDLE_MS,
} from './useJobsListPolling.utils';

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

type Options = {
  params: JobListParams;
  enabled?: boolean;
};

export function useJobsListPolling({ params, enabled = true }: Options) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [summary, setSummary] = useState<JobSummary | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const abortRef = useRef<AbortController | null>(null);
  const summaryRef = useRef<JobSummary | null>(null);
  const jobsRef = useRef<Job[]>([]);
  const recentActivityUntilRef = useRef(0);

  const markRecentJobActivity = useCallback(() => {
    recentActivityUntilRef.current = Date.now() + JOBS_POLL_BURST_WINDOW_MS;
  }, []);

  const pollShouldBeFast = useCallback(
    (summary: JobSummary | null, jobs: Job[], data?: { summary: JobSummary; jobs: Job[] } | null) => {
      if (Date.now() < recentActivityUntilRef.current) return true;
      if (data) {
        const prevTotal = summaryTotalJobs(summaryRef.current);
        const nextTotal = summaryTotalJobs(data.summary);
        if (nextTotal > prevTotal) return true;
        const prevIds = new Set(jobsRef.current.map((job) => job.id));
        if (data.jobs.some((job) => !prevIds.has(job.id))) return true;
      }
      return shouldPollJobsFast(summary, jobs);
    },
    []
  );

  const fetchDashboard = useCallback(
    async (background: boolean) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      if (!background) {
        setLoading(true);
      }

      try {
        const data = await api.jobs.dashboard(paramsRef.current, { signal: controller.signal });
        if (controller.signal.aborted) return data;
        const prevJobs = jobsRef.current;
        const prevTotal = summaryTotalJobs(summaryRef.current);
        const nextTotal = summaryTotalJobs(data.summary);
        if (
          prevJobs.length > 0 &&
          ((prevTotal > 0 && nextTotal > prevTotal) ||
            data.jobs.some((job) => !prevJobs.some((prev) => prev.id === job.id)))
        ) {
          markRecentJobActivity();
        }
        setJobs((prev) => {
          const merged = mergeJobRows(prev, data.jobs);
          jobsRef.current = merged;
          return merged;
        });
        setSummary(data.summary);
        summaryRef.current = data.summary;
        setTotal(data.total ?? data.jobs.length);
        setError(null);
        return data;
      } catch (e) {
        if (isAbortError(e) || controller.signal.aborted) return null;
        setError(e instanceof Error ? e.message : 'Failed to load jobs');
        return null;
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    },
    [markRecentJobActivity]
  );

  const autoRefresh = useAutoRefresh({
    enabled,
    intervalMs: pollIntervalMs(false, true),
    pauseWhenHidden: false,
    getIntervalMs: () =>
      pollIntervalMs(
        pollShouldBeFast(summaryRef.current, jobsRef.current),
        document.visibilityState === 'visible'
      ),
    onRefresh: async ({ background }) => {
      await fetchDashboard(background);
    },
    deps: [params],
    storageKey: 'jobs-auto-refresh',
  });

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
    }
  }, [enabled]);

  return {
    jobs,
    summary,
    total,
    loading,
    error,
    autoRefreshing: autoRefresh.refreshing,
    lastUpdatedAt: autoRefresh.lastUpdatedAt,
    refresh: autoRefresh.refresh,
    autoRefresh,
  };
}
